import { UploadTask as ExpoUploadTask } from 'expo-file-system/legacy';
import { Err, err, Ok, ok } from 'neverthrow';
import { v4 as uuidv4 } from 'uuid';

import MediaFile from '../MediaFile';
import {
  AttachMediaOptions,
  UploadedMedia,
  UploadMediaOptions,
  UploadMediaResult,
  UploadTaskListeners,
  UploadTaskStatus,
} from '../types';

import MediaUploadError from './MediaUploadError';

const DEFAULT_SECONDS_BETWEEN_AUTO_RETRIES = 10;

// util functions to refine UploadTask instances
export const isInProgressUploadTask = (task: UploadTask): task is ProgressUploadTask =>
  task.status === 'in progress' || task.status === 'queued';

export const isSuccessfulUploadTask = (task: UploadTask): task is SuccessfulUploadTask =>
  task.status === 'complete' && !!task.result?.isOk();

export const isFailedUploadTask = (task: UploadTask): task is FailedUploadTask =>
  task.status === 'errored' && !!task.result?.isErr();

export const isCancelledUploadTask = (task: UploadTask): task is CancelledUploadTask =>
  task.status === 'cancelled' && !!task.result?.isOk();

export default class UploadTask {
  file: MediaFile;

  id: string;

  options: UploadMediaOptions;

  progress: number;

  retryCount: number;

  result?: UploadMediaResult;

  status: UploadTaskStatus;

  listeners: Record<string, UploadTaskListeners>;

  expoTask?: ExpoUploadTask;

  constructor(mediaFile: MediaFile, options?: UploadMediaOptions, listeners?: UploadTaskListeners) {
    // suppress noisy logs during testing
    /* istanbul ignore next */
    if (!process.env.JEST_WORKER_ID) {
      // eslint-disable-next-line no-console
      console.debug(`constructing new UploadTask for ${mediaFile.name}`);
    }
    this.file = mediaFile;
    this.id = mediaFile.id; // convenient access to the unique id of the media file
    this.options = options || {};
    this.progress = 0; // progress is always 0 at the start
    this.listeners = {};
    this.retryCount = 0; // retryCount starts at 0
    this.status = 'queued'; // upload tasks always start queued
    this.expoTask = undefined;

    if (listeners) {
      this.attachListeners(listeners);
    }
  }

  // private state setters, these also call the appropriate listeners
  private setStatus = (nextStatus: UploadTaskStatus): void => {
    this.status = nextStatus;
    this.handleOnProgress();
  };

  private setProgress = (nextProgress: number): void => {
    this.progress = nextProgress;
    this.handleOnProgress();
  };

  private setResult = (nextResult: UploadMediaResult): void => {
    this.result = nextResult;
    this.handleOnFinish();
  };

  // private methods to call attached listeners
  private handleOnProgress(): void {
    // call all onProgress listeners
    Object.values(this.listeners).forEach((i) => i.onProgress?.(this));
  }

  private handleOnFinish(): void {
    // call all onFinish listeners
    Object.values(this.listeners).forEach((i) => i.onFinish?.(this));
    this.listeners = {}; // delete all listeners
  }

  private handleOnCancel(): void {
    // call all onCancel listeners
    Object.values(this.listeners).forEach((i) => i.onCancel?.(this));
  }

  // public methods, mostly to go from one task status to another
  public start(): void {
    this.progress = 0; // re-zero progress just to be safe
    this.setStatus('in progress');
  }

  public updateProgress(progress: number): void {
    this.setProgress(progress);
  }

  private shouldRetry(error: MediaUploadError): boolean {
    return (
      !error.permanent &&
      this.options.timesToAutoRetry !== undefined &&
      this.options.timesToAutoRetry > 0 &&
      this.options.timesToAutoRetry > this.retryCount
    );
  }

  public complete(finalUploadUrl: string): void {
    this.status = 'complete'; // can set this directly because we don't need the handleOnProgress that setStatus provides
    this.setResult(ok({ url: finalUploadUrl }));
  }

  public async fail(error: MediaUploadError): Promise<void> {
    if (this.shouldRetry(error)) {
      // determine how long to wait before retrying the upload
      const seconds =
        this.options.secondsBetweenAutoRetries ?? DEFAULT_SECONDS_BETWEEN_AUTO_RETRIES;
      const ms = seconds * 1000;
      setTimeout(() => {
        this.retryCount += 1;
        this.setProgress(0);
        // the task.status is still 'in progress'
        Object.values(this.listeners).forEach((i) => i.onRetry?.(this));
      }, ms);
    } else {
      this.status = 'errored'; // can set this directly because we don't need the handleOnProgress that setStatus provides
      this.setResult(err(error));
    }
  }

  public async cancel(): Promise<void> {
    this.result = ok({ url: '' }); // cancelling is an "ok" result but has no result url
    this.status = 'cancelled'; // can set this directly because we don't need the handleOnProgress that setStatus provides
    await this.expoTask?.cancelAsync(); // only set on native so safe to call
    this.handleOnCancel();
  }

  // simple public setter for the expoTask
  public setExpoTask = (expoTask: ExpoUploadTask): void => {
    this.expoTask = expoTask;
  };

  // returns an unattach func
  attachListeners(listeners: UploadTaskListeners): () => void {
    const listenerId = uuidv4();
    this.listeners[listenerId] = listeners;
    return () => this.removeListeners(listenerId);
  }

  removeListeners(listenerId: string): void {
    if (this.listeners[listenerId]) {
      delete this.listeners[listenerId];
    }
  }

  // methods for self-refining UploadTask instances
  isSuccessful(): this is SuccessfulUploadTask {
    return isSuccessfulUploadTask(this);
  }

  isInProgress(): this is ProgressUploadTask {
    return isInProgressUploadTask(this);
  }

  isFailed(): this is FailedUploadTask {
    return isFailedUploadTask(this);
  }

  isCancelled(): this is CancelledUploadTask {
    return isCancelledUploadTask(this);
  }

  isAttachable(): this is AttachableUploadTask {
    const isOk = this.result?.isOk();
    const { attach } = this.options;
    return !!(isOk && attach);
  }
}

export interface ProgressUploadTask extends UploadTask {
  status: 'in progress' | 'queued';
  progress: number;
}
export interface SuccessfulUploadTask extends UploadTask {
  status: 'complete';
  result: Ok<UploadedMedia, MediaUploadError>;
}

export interface FailedUploadTask extends UploadTask {
  status: 'errored';
  result: Err<UploadedMedia, MediaUploadError>;
}

export interface CancelledUploadTask extends UploadTask {
  status: 'cancelled';
  result: Ok<UploadedMedia, MediaUploadError>;
}

export interface AttachableUploadTask extends SuccessfulUploadTask {
  options: {
    attach: AttachMediaOptions;
  };
}
